import React, { useEffect, useState } from 'react';
import { useContent } from '../contexts/ContentContext';
import { publicUrl } from '../services/publicRecords';
import ContentPageHeader from './PageHeader';

interface RelatedLink {
  title: string;
  url: string;
  description?: string;
  icon_path?: string;
}

const RelatedLinks: React.FC = () => {
  const { t } = useContent();
  const [links, setLinks] = useState<RelatedLink[]>([]);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const normalizeIconPath = (path?: string) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    let normalized = path;
    if (normalized.startsWith('./')) {
      normalized = normalized.slice(1);
    }
    if (normalized.startsWith('public/')) {
      normalized = normalized.slice('public'.length);
    }
    if (!normalized.startsWith('/')) {
      normalized = `/${normalized}`;
    }
    return normalized;
  };

  useEffect(() => {
    setLoading(true); setError(false);
    fetch('/related_links.json')
      .then(res => { if (!res.ok) throw new Error('Related links HTTP'); return res.json(); })
      .then(data => {
        if (!Array.isArray(data)) throw new Error('Invalid related links');
        setLinks(data.filter(link => typeof link.title === 'string' && publicUrl(link.url)));
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch related links:", err);
        setError(true);
        setLoading(false);
      });
  }, [attempt]);

  if (loading) {
    return <><ContentPageHeader /><p role="status">{t('common.loading')}</p></>;
  }
  if (error) return <><ContentPageHeader /><div role="alert"><p>{t('common.error')}</p><button className="lab-button" onClick={() => setAttempt(value => value + 1)}>{t('common.retry')}</button></div></>;

  return (
    <div className="lab-page lab-content-page">
      <ContentPageHeader />
      <ul className="space-y-3">
        {links.map((link, index) => (
          <li key={index} className="flex items-start gap-3">
            {link.icon_path ? (
              <img
                src={normalizeIconPath(link.icon_path)}
                alt=""
                className="w-10 h-10 rounded object-cover border border-gray-200 dark:border-gray-700"
                loading="lazy"
              />
            ) : (
              <div className="w-10 h-10 rounded bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600" />
            )}
            <div className="min-w-0">
              <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                {link.title}
              </a>
              {link.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{link.description}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RelatedLinks;
